import React, { useCallback, useEffect, useState } from 'react';
import {
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetFooter
} from '@inter-ikea-kompis/react-components';
import styled from 'styled-components';
import media from 'ui/media';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Title from 'ui/Typography/Title';
import SlideOutMenu from 'components/SlideOutMenu/SlideOutMenu';
import Box from 'ui/Box';
import { useIntl } from 'react-intl';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { setSavePossible } from 'ducks/layout';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import { getMarketProducts } from 'ducks/products';
import { removeProducts } from 'rangeConfig/shared/utils/removeProducts';
import { getMarketMaxDrawerHeight } from 'rangeConfig/smastad/rules/interiorPlacementRules';
import InvalidInterior from 'ui/images/InvalidInterior.jpg';
import { getCountry } from 'i18n/i18nUtils';
import {
  InteriorPlacementValidationSystem,
  InvalidPlacedInteriorComponent
} from '@inter-ikea-storageone/range-smastad';
import { useSelector } from 'react-redux';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

const TitleWrapper = styled.div`
  ${media.mobile`
    width: 100%;
    padding: 0.5rem 0;
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const ImageWrapper = styled(Box)`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const Image = styled.img`
  max-width: 100%;
`;

/**
 * Menu is displayed in case a entity is placed in a invalid place
 * and needs to be corrected or removed
 */
const InvalidPlacedEntityMenu = () => {
  const [visible, setVisible] = useState(false);
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const products = useSelector(getMarketProducts);

  const onHistoryEvent = useCallback(() => {
    const invalidPlacements =
      coreApi.entities.getByComponent(InvalidPlacedInteriorComponent.id).length > 0;

    setVisible(invalidPlacements);
    dispatch(setSavePossible(!invalidPlacements));
  }, [coreApi, dispatch]);

  useEffect(() => {
    const maxHeight = getMarketMaxDrawerHeight();

    coreApi
      .getSystem<InteriorPlacementValidationSystem>('InteriorPlacementValidationSystem')
      ?.setMarketMaxAllowedHeight(maxHeight, getCountry());
  }, [coreApi]);

  useEffect(() => {
    const unsubscribers = [
      coreApi.subscribe('history', onHistoryEvent),
      coreApi.subscribe('reset', onHistoryEvent),
      coreApi.subscribe('load', onHistoryEvent)
    ];
    return () => {
      unsubscribers.forEach(unsubscribe => {
        unsubscribe();
      });
    };
  }, [coreApi, onHistoryEvent]);

  const onRemove = () => {
    coreApi.history.historyAction(() => {
      const invalidPlacements = coreApi.entities.getByComponent(InvalidPlacedInteriorComponent.id);
      removeProducts(invalidPlacements, coreApi, products);
    });
  };

  const onUndo = () => {
    coreApi.history.undo();
  };

  const canUndo = coreApi.history.canUndo();

  return (
    <SlideOutMenu trigger={!!visible}>
      <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <KompisSheetBody theme={SkapaTheme}>
          <KompisSheetBodyPadding theme={SkapaTheme}>
            <TitleWrapper>
              <Title pb="s" pt="l" type={ThemeFontStyleTypeEnum.headingMedium}>
                {formatMessage({ id: 'storageone.interiorsTooHighTitle' })}
              </Title>
            </TitleWrapper>
            <Body py="md" mb="xxxl" type={ThemeFontStyleTypeEnum.bodyMedium}>
              {formatMessage({ id: 'storageone.interiorsTooHighDescription' })}
            </Body>
            <ImageWrapper>
              <Image
                src={InvalidInterior}
                alt={formatMessage({ id: 'storageone.summaryTabSafetyImageDescription' })}
              />
            </ImageWrapper>
          </KompisSheetBodyPadding>
        </KompisSheetBody>
        <KompisSheetFooter theme={SkapaTheme}>
          <ButtonWrapper>
            {canUndo && (
              <Button
                type={ButtonTypeEnum.primary}
                text={formatMessage({ id: 'storageone.sceneMenuHistoryUndo' })}
                dataTestId={'invalid-placed-component-menu-undo-btn'}
                onClick={onUndo}
                width={'100%'}
                mb="md"
              />
            )}
            <Button
              type={canUndo ? ButtonTypeEnum.secondary : ButtonTypeEnum.primary}
              text={formatMessage({ id: 'storageone.productListRemoveButton' })}
              dataTestId={'invalid-placed-component-menu-remove-components-btn'}
              onClick={onRemove}
              width={'100%'}
            />
          </ButtonWrapper>
        </KompisSheetFooter>
      </Box>
    </SlideOutMenu>
  );
};

export default InvalidPlacedEntityMenu;
